import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getStoredFilters, saveStoredFilters } from "services/listFilterStorage";

const VALID_STATUSES = ["all", "active", "inactive"];
function paramsToFilters(params, defaultStatus) {
  return {
    page: Math.max(1, Number(params.get("page")) || 1),
    status: VALID_STATUSES.includes(params.get("status")) ? params.get("status") : defaultStatus,
    showDeleted: params.get("show_deleted") === "true",
  };
}

function useListPageQuery(listKey, { defaultStatus = "active", defaultShowDeleted = false } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location);
  locationRef.current = location;

  const isHydratedRef = useRef(false);
  const hasHydratedFromStorageRef = useRef(false);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  const statusParam = searchParams.get("status");
  const status = VALID_STATUSES.includes(statusParam) ? statusParam : defaultStatus;

  const showDeleted = searchParams.get("show_deleted") === "true";

  const navigateWithParams = useCallback(
    (params) => {
      const { pathname } = locationRef.current;
      const query = params.toString();
      navigate(query ? `${pathname}?${query}` : pathname, { replace: true });
    },
    [navigate]
  );

  // Restore missing query params from localStorage once when the list page mounts.
  useEffect(() => {
    if (hasHydratedFromStorageRef.current) return;

    const params = new URLSearchParams(location.search);
    const stored = getStoredFilters(listKey);
    let changed = false;

    if (!params.has("page")) {
      const storedPage = Math.max(1, Number(stored.page) || 1);
      if (storedPage > 1) {
        params.set("page", String(storedPage));
        changed = true;
      }
    }

    if (!params.has("status")) {
      const storedStatus = VALID_STATUSES.includes(stored.status) ? stored.status : defaultStatus;
      if (storedStatus !== defaultStatus) {
        params.set("status", storedStatus);
        changed = true;
      }
    }

    if (!params.has("show_deleted")) {
      if (stored.showDeleted === true) {
        params.set("show_deleted", "true");
        changed = true;
      }
    }

    hasHydratedFromStorageRef.current = true;
    isHydratedRef.current = true;

    if (changed) {
      navigateWithParams(params);
      saveStoredFilters(listKey, paramsToFilters(params, defaultStatus));
    } else {
      saveStoredFilters(listKey, paramsToFilters(params, defaultStatus));
    }
  }, [listKey, location.search, defaultStatus, navigateWithParams]);

  // Keep localStorage in sync with the URL after hydration (never rewrite the URL here).
  useEffect(() => {
    if (!isHydratedRef.current) return;
    saveStoredFilters(listKey, paramsToFilters(searchParams, defaultStatus));
  }, [listKey, defaultStatus, searchParams]);

  const setPage = useCallback(
    (newPage) => {
      const params = new URLSearchParams(locationRef.current.search);
      const resolvedPage = Math.max(1, Number(newPage) || 1);

      if (resolvedPage <= 1) {
        params.delete("page");
      } else {
        params.set("page", String(resolvedPage));
      }

      const filters = paramsToFilters(params, defaultStatus);
      saveStoredFilters(listKey, filters);
      navigateWithParams(params);
    },
    [listKey, defaultStatus, navigateWithParams]
  );

  const resetPage = useCallback(() => {
    setPage(1);
  }, [setPage]);

  const setStatus = useCallback(
    (newStatus) => {
      const params = new URLSearchParams(locationRef.current.search);

      if (newStatus === defaultStatus) {
        params.delete("status");
      } else {
        params.set("status", newStatus);
      }

      params.delete("page");

      const filters = {
        ...paramsToFilters(params, defaultStatus),
        status: newStatus,
        page: 1,
      };
      saveStoredFilters(listKey, filters);
      navigateWithParams(params);
    },
    [listKey, defaultStatus, navigateWithParams]
  );

  const setShowDeleted = useCallback(
    (newShowDeleted) => {
      const params = new URLSearchParams(locationRef.current.search);

      if (!newShowDeleted) {
        params.delete("show_deleted");
      } else {
        params.set("show_deleted", "true");
      }

      params.delete("page");

      const filters = {
        ...paramsToFilters(params, defaultStatus),
        showDeleted: newShowDeleted,
        page: 1,
      };
      saveStoredFilters(listKey, filters);
      navigateWithParams(params);
    },
    [listKey, defaultStatus, navigateWithParams]
  );

  return { page, setPage, resetPage, status, setStatus, showDeleted, setShowDeleted };
}

export default useListPageQuery;
